/**
 * "Group these for me" in the classification studio.
 *
 * The pure decisions live in `utils/autoGroup.js`; this is the part that has to
 * touch the browser — fetching bytes, shrinking them, and writing the result
 * into the store.
 *
 * Two things here are easy to get wrong:
 *
 * - **An unlabeled image is one of two shapes.** A freshly uploaded one carries
 *   a real `File`; one loaded from a saved version carries only a URL, because
 *   the bytes are in MinIO. Both must work, so the blob is taken from `file`
 *   when it is there and fetched when it is not.
 *
 * - **Grouping must not create classes it then fails to fill.** The classes are
 *   created FIRST, from the count the server actually produced, and only then
 *   are images moved — so a failure leaves the pool untouched rather than
 *   half-sorted into empty classes.
 */
import { useCallback, useRef, useState } from 'react';
import useClassificationStore from '../store/useClassificationStore';
import { autoGroupAPI } from '../api/classificationLabelApi';
import { canGroup, planAssignments, splitForGrouping } from '../utils/autoGroup';
import { downscaleImage } from '../../../../assistant/utils/imageDownscale';

/**
 * Shown when the groups came back but the classes to hold them could not be
 * created. A literal rather than an i18n key: this hook has no `t`, and a
 * failure the user can act on beats a key rendered raw at them.
 */
const CLASSES_FAILED_MESSAGE = 'Could not create the classes for these groups.';

/** Fetch the bytes for an image that was loaded from a saved version. */
const blobForImage = async (image, signal) => {
  if (image.file) return image.file;
  const url = image.url_display || image.thumbnail_url_display || image.url;
  if (!url) throw new Error(`No image data for ${image.name || image.id}`);
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`Could not read ${image.name || image.id}`);
  return response.blob();
};

const useAutoGroup = () => {
  const classes = useClassificationStore((state) => state.classes);
  const unstructuredImages = useClassificationStore((state) => state.unstructuredImages);

  const [state, setState] = useState({
    running: false, phase: null, done: 0, total: 0, error: null, result: null,
  });
  const abortRef = useRef(null);

  const cancel = useCallback(() => abortRef.current?.abort(), []);

  const run = useCallback(
    async ({ numClusters, maxClusters } = {}) => {
      const {
        addClasses, classes: currentClasses, unstructuredImages: pool,
        addClassImage, removeUnstructuredImages,
      } = useClassificationStore.getState();

      if (!canGroup(pool)) {
        setState((s) => ({ ...s, error: 'notEnough' }));
        return null;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      const { sample, untouched } = splitForGrouping(pool);
      setState({ running: true, phase: 'reading', done: 0, total: sample.length, error: null, result: null });

      try {
        // 1. Bytes. Shrunk before they go on the wire — the server resizes to
        //    256 and centre-crops to 224 anyway, so this changes the grouping
        //    not at all while cutting the upload by roughly fifty times.
        const files = [];
        for (let i = 0; i < sample.length; i += 1) {
          if (controller.signal.aborted) throw new DOMException('cancelled', 'AbortError');
          // eslint-disable-next-line no-await-in-loop
          const raw = await blobForImage(sample[i], controller.signal);
          // eslint-disable-next-line no-await-in-loop
          const blob = await downscaleImage(raw);
          files.push({ blob, name: sample[i].name || `${sample[i].id}.jpg` });
          setState((s) => ({ ...s, done: i + 1 }));
        }

        // 2. Group.
        setState((s) => ({ ...s, phase: 'grouping' }));
        const data = await autoGroupAPI({
          files, numClusters, maxClusters, signal: controller.signal,
        });

        // 3. Create the classes the server actually produced, THEN fill them.
        const plan = planAssignments({
          assignments: data.assignments, sample, classes: currentClasses,
        });
        if (plan.classesNeeded > 0) addClasses(plan.classesNeeded);

        const after = useClassificationStore.getState().classes;

        /*
         * The classes MUST exist before the images move.
         *
         * `planAssignments` maps cluster -> class by position and returns a null
         * classId past the end of the list, and the loop below skips a null
         * silently. So a class-creation that quietly does nothing does not fail
         * — it drops the tail of the pool and reports success for whatever was
         * left. That is exactly what happened while `addClasses` ignored its
         * argument: zero classes were created and not one image moved, with only
         * a console.warn to show for it.
         */
        if (after.length < plan.groups.length) {
          setState({
            running: false, phase: 'failed', done: 0, total: 0,
            error: CLASSES_FAILED_MESSAGE,
            result: null,
          });
          return null;
        }
        const filled = planAssignments({
          assignments: data.assignments, sample, classes: after,
        });

        const moved = [];
        filled.perImage.forEach((entry) => {
          if (entry.classId == null) return;
          const image = sample.find((candidate) => candidate.id === entry.imageId);
          if (!image) return;
          addClassImage(entry.classId, { ...image, assignedClass: entry.classId });
          moved.push(entry.imageId);
        });
        if (moved.length) removeUnstructuredImages(moved);

        const result = {
          grouped: moved.length,
          groups: filled.groups.length,
          untouched: untouched.length,
          skipped: (data.skipped || []).length,
        };
        setState({ running: false, phase: 'done', done: sample.length, total: sample.length, error: null, result });
        return result;
      } catch (error) {
        const cancelled = error?.name === 'AbortError' || error?.code === 'ERR_CANCELED';
        setState({
          running: false, phase: cancelled ? 'cancelled' : 'failed',
          done: 0, total: 0,
          error: cancelled ? null : error?.message || 'Grouping failed.',
          result: null,
        });
        return null;
      } finally {
        abortRef.current = null;
      }
    },
    [],
  );

  return {
    ...state,
    run,
    cancel,
    canGroup: canGroup(unstructuredImages),
    unlabeledCount: (unstructuredImages || []).length,
    classCount: (classes || []).length,
  };
};

export default useAutoGroup;
